<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import { pageById } from '@/data/registry'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { adapter, type DashboardStats } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { formatDateTime, formatMoney, formatNumber, formatPercent } from '@/utils/format'
import { downloadBlob } from '@/utils/export'
import EChart from '@/components/ui/EChart.vue'
import AppIcon from '@/components/AppIcon.vue'

const definition = pageById('analytics')
if (!definition) throw new Error('经营分析页面定义缺失')

const auth = useAuthStore()
const ui = useUiStore()
const router = useRouter()
const period = ref<'7' | '30' | '90'>('7')
const dimension = ref<'all' | 'users' | 'records' | 'alerts'>('all')
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const stats = ref<DashboardStats | null>(null)
const updatedAt = ref('')
const startedAt = Date.now()
const canExport = computed(() => auth.has('analytics.export'))

const BASE = new Date('2026-10-17T12:00:00+08:00').getTime()

function dateString(daysAgo: number): string {
  const date = new Date(BASE - daysAgo * 86_400_000)
  const local = new Date(date.getTime() + 8 * 3_600_000)
  return `${local.getUTCFullYear()}-${String(local.getUTCMonth() + 1).padStart(2, '0')}-${String(local.getUTCDate()).padStart(2, '0')}`
}

const start = computed(() => dateString(Number(period.value) - 1))
const end = computed(() => dateString(0))
const rangeLabel = computed(() => `${start.value.replace(/-/g, '/')} 至 ${end.value.replace(/-/g, '/')}`)

const kpis = computed(() => {
  const value = stats.value
  if (!value) return []
  return [
    { key: 'users', label: '平台用户', value: formatNumber(value.usersTotal), foot: `活跃 ${formatNumber(value.activeUsers)} 人`, tone: 'brand', icon: 'users' },
    { key: 'families', label: '家庭档案', value: formatNumber(value.families), foot: `在线率 ${formatPercent(value.onlineRate)}`, tone: 'info', icon: 'family' },
    { key: 'adherence', label: '用药依从率', value: formatPercent(value.adherence), foot: `预警待处理 ${formatNumber(value.pendingAlerts)} 条`, tone: 'success', icon: 'pill' },
    { key: 'revenue', label: '会员实付', value: formatMoney(value.revenueFen), foot: `已支付 ${formatNumber(value.paidOrders)} 单`, tone: 'purple', icon: 'crown' },
    { key: 'devices', label: '设备总数', value: formatNumber(value.deviceTotal), foot: `高风险预警 ${formatNumber(value.highAlerts)} 条`, tone: 'cyan', icon: 'device' },
    { key: 'risk', label: '待处理风险', value: formatNumber(value.highAlerts + value.urgentTickets), foot: `紧急工单 ${formatNumber(value.urgentTickets)} 个`, tone: 'danger', icon: 'alert' },
  ]
})

const trendSeries = computed(() => {
  const trend = stats.value?.trend ?? []
  const all = [
    { key: 'users', name: '活跃用户', color: '#08b578', data: trend.map(item => item.users) },
    { key: 'records', name: '健康记录', color: '#2f86eb', data: trend.map(item => item.records) },
    { key: 'alerts', name: '健康预警', color: '#f04444', data: trend.map(item => item.alerts) },
  ]
  return dimension.value === 'all' ? all : all.filter(item => item.key === dimension.value)
})

const trendOption = computed<EChartsOption>(() => {
  const trend = stats.value?.trend ?? []
  return {
    animationDuration: 420,
    grid: { left: 40, right: 18, top: 24, bottom: 28 },
    legend: { top: 0, right: 0, itemWidth: 8, itemHeight: 8, textStyle: { fontSize: 10, color: '#7183a1' } },
    tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trend.map(item => item.day.slice(5)),
      axisLine: { lineStyle: { color: '#e8eef4' } },
      axisTick: { show: false },
      axisLabel: { color: '#9aa8bd', fontSize: 10 },
    },
    yAxis: { type: 'value', splitNumber: 4, axisLabel: { color: '#9aa8bd', fontSize: 10 }, splitLine: { lineStyle: { color: '#f0f4f8', type: 'dashed' } } },
    series: trendSeries.value.map((item, index) => ({
      name: item.name,
      type: 'line' as const,
      smooth: true,
      symbolSize: 4,
      data: item.data,
      lineStyle: { color: item.color, width: index === 0 ? 2 : 1.6 },
      itemStyle: { color: item.color },
      areaStyle: dimension.value === 'all' && item.key === 'users' ? { color: 'rgba(8,181,120,.1)' } : undefined,
    })),
  }
})

const channelOption = computed<EChartsOption>(() => {
  const channels = stats.value?.channels ?? []
  return {
    animationDuration: 420,
    tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
    legend: { bottom: 0, itemWidth: 8, itemHeight: 8, textStyle: { fontSize: 10, color: '#7183a1' } },
    series: [{
      type: 'pie',
      radius: ['46%', '68%'],
      center: ['50%', '44%'],
      label: { show: false },
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      data: channels.map(item => ({ name: item.label, value: item.value })),
    }],
  }
})

const riskOption = computed<EChartsOption>(() => {
  const risk = stats.value?.risk ?? []
  return {
    animationDuration: 420,
    grid: { left: 54, right: 20, top: 12, bottom: 20 },
    tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
    xAxis: { type: 'value', splitLine: { lineStyle: { color: '#f0f4f8', type: 'dashed' } }, axisLabel: { color: '#9aa8bd', fontSize: 10 } },
    yAxis: { type: 'category', data: risk.map(item => item.label), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#7183a1', fontSize: 10 } },
    series: [{
      type: 'bar',
      data: risk.map(item => ({
        value: item.value,
        itemStyle: { color: item.tone === 'danger' ? '#f04444' : item.tone === 'warning' ? '#f5a623' : '#08b578', borderRadius: [0, 4, 4, 0] },
      })),
      barWidth: 12,
    }],
  }
})

const regionRows = computed(() => (stats.value?.regions ?? []).slice(0, 8))

async function load(mode: 'initial' | 'refresh' = 'initial'): Promise<void> {
  if (mode === 'initial') loading.value = true
  else refreshing.value = true
  error.value = ''
  try {
    const result = await request(
      () => adapter.dashboardStats(start.value, end.value, ui.dataScopeId || undefined),
      { delay: mode === 'initial' ? 260 : 160 },
    )
    stats.value = result
    updatedAt.value = formatDateTime(new Date())
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : '分析数据加载失败'
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

function exportReport(): void {
  if (!canExport.value) {
    ui.toast('warning', '没有导出权限', '当前角色未获授经营分析导出权限。')
    return
  }
  const value = stats.value
  if (!value) return
  const lines = [
    '康护健康 · 经营分析',
    `统计周期,${rangeLabel.value}`,
    `导出时间,${formatDateTime(new Date())}`,
    '',
    '指标,数值',
    `平台用户,${value.usersTotal}`,
    `活跃用户,${value.activeUsers}`,
    `家庭档案,${value.families}`,
    `用药依从率,${value.adherence}%`,
    `待处理预警,${value.pendingAlerts}`,
    `在线率,${value.onlineRate}%`,
    `会员实付（分）,${value.revenueFen}`,
    `已支付订单,${value.paidOrders}`,
    '',
    '区域,用户数,家庭数,预警数',
    ...value.regions.map(item => `${item.label},${item.users},${item.families},${item.alerts}`),
  ]
  downloadBlob(`康护健康-经营分析-${start.value}-${end.value}.csv`, `\uFEFF${lines.join('\n')}`)
  ui.toast('success', '分析报表已导出', '导出范围包含当前周期与数据范围。')
}

function go(route: string): void {
  void router.push(route)
}

onMounted(() => { void load() })
</script>

<template>
  <section class="analytics-page">
    <header class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">经营分析</h1>
          <span class="prototype-pill">原型数据</span>
        </div>
        <p class="page-subtitle">{{ definition.purpose }}</p>
      </div>
      <div class="page-header__actions">
        <div class="segmented" role="tablist" aria-label="统计周期">
          <button v-for="item in [{ key: '7', label: '近7天' }, { key: '30', label: '近30天' }, { key: '90', label: '近90天' }]" :key="item.key" type="button" class="segmented__item" :class="{ 'segmented__item--active': period === item.key }" @click="period = item.key as '7' | '30' | '90'; void load('refresh')">{{ item.label }}</button>
        </div>
        <button type="button" class="btn" :disabled="refreshing" @click="load('refresh')">
          <span v-if="refreshing" class="button-spinner" aria-hidden="true" /><AppIcon v-else name="refresh" :size="14" />刷新
        </button>
        <button v-if="canExport" type="button" class="btn btn--primary" @click="exportReport"><AppIcon name="export" :size="14" />导出分析</button>
      </div>
    </header>

    <div class="page-toolbar">
      <span class="muted">统计周期：{{ rangeLabel }}</span>
      <span class="muted">数据范围：{{ ui.dataScopeId ? '当前区域' : '全部区域' }}</span>
      <span class="page-toolbar__spacer" />
      <span class="faint">最近刷新 {{ updatedAt || '—' }}</span>
    </div>

    <div v-if="loading" class="app-loading" style="min-height: 360px">
      <div class="app-loading__card"><span class="app-loading__ring" /><span>正在计算运营指标…</span></div>
    </div>

    <div v-else-if="error" class="empty-state" role="alert">
      <span class="empty-state__art"><AppIcon name="warning" :size="44" /></span>
      <h3 class="empty-state__title">分析数据加载失败</h3>
      <p class="empty-state__text">{{ error }}</p>
      <div class="empty-state__actions"><button type="button" class="btn btn--primary" @click="load()">重试</button></div>
    </div>

    <template v-else>
      <section class="dash-kpis" aria-label="经营指标">
        <button v-for="kpi in kpis" :key="kpi.key" type="button" class="dash-kpi" :class="`dash-kpi--${kpi.tone}`" @click="go(kpi.key === 'revenue' ? '/membership/orders' : kpi.key === 'devices' ? '/devices' : kpi.key === 'users' ? '/users' : '/dashboard')">
          <span class="dash-kpi__icon"><AppIcon :name="kpi.icon" :size="20" /></span>
          <span class="dash-kpi__body">
            <span class="dash-kpi__label">{{ kpi.label }}</span>
            <strong class="dash-kpi__value">{{ kpi.value }}</strong>
            <span class="dash-kpi__foot">{{ kpi.foot }}</span>
          </span>
        </button>
      </section>

      <section class="dash-grid dash-grid--first">
        <article class="card dash-panel">
          <header class="dash-panel__head">
            <div><h2 class="dash-panel__title">运营趋势</h2><p class="dash-panel__sub">用户活跃、健康记录与预警触发</p></div>
            <div class="segmented" style="margin-left:auto" role="tablist" aria-label="分析维度">
              <button v-for="item in [{ key: 'all', label: '全部指标' }, { key: 'users', label: '活跃用户' }, { key: 'records', label: '健康记录' }, { key: 'alerts', label: '健康预警' }]" :key="item.key" type="button" class="segmented__item" :class="{ 'segmented__item--active': dimension === item.key }" @click="dimension = item.key as 'all' | 'users' | 'records' | 'alerts'">{{ item.label }}</button>
            </div>
          </header>
          <EChart :option="trendOption" height="278px" aria-label="运营趋势折线图" />
        </article>
        <article class="card dash-panel">
          <header class="dash-panel__head">
            <div><h2 class="dash-panel__title">用户来源渠道</h2><p class="dash-panel__sub">按注册渠道聚合</p></div>
          </header>
          <EChart :option="channelOption" height="278px" aria-label="用户来源渠道环形图" />
        </article>
        <article class="card dash-panel">
          <header class="dash-panel__head">
            <div><h2 class="dash-panel__title">风险分布</h2><p class="dash-panel__sub">当前未关闭风险构成</p></div>
          </header>
          <EChart :option="riskOption" height="278px" aria-label="风险分布条形图" />
        </article>
      </section>

      <section class="dash-grid dash-grid--second">
        <article class="card dash-panel">
          <header class="dash-panel__head">
            <div><h2 class="dash-panel__title">区域对比</h2><p class="dash-panel__sub">用户、家庭与预警的区域分布</p></div>
          </header>
          <div class="table-scroll">
            <table class="pro-table">
              <thead><tr><th>区域</th><th>用户数</th><th>家庭数</th><th>预警数</th><th>操作</th></tr></thead>
              <tbody>
                <tr v-for="row in regionRows" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td class="is-num">{{ formatNumber(row.users) }}</td>
                  <td class="is-num">{{ formatNumber(row.families) }}</td>
                  <td class="is-num">{{ formatNumber(row.alerts) }}</td>
                  <td><button type="button" class="row-action" @click="go('/health/alerts')">查看预警</button></td>
                </tr>
                <tr v-if="!regionRows.length"><td colspan="5" class="cell-muted">当前周期暂无区域数据</td></tr>
              </tbody>
            </table>
          </div>
        </article>
        <article class="card dash-panel">
          <header class="dash-panel__head">
            <div><h2 class="dash-panel__title">服务与任务</h2><p class="dash-panel__sub">异步任务与客服 SLA</p></div>
          </header>
          <div class="dash-family" style="grid-template-columns: 1fr">
            <div class="dash-family__kpis">
              <div><span>待审核药品</span><b>{{ formatNumber(stats?.pendingDrugs) }}</b><small>进入药事审核队列</small></div>
              <div><span>紧急工单</span><b>{{ formatNumber(stats?.urgentTickets) }}</b><small>按 SLA 优先处理</small></div>
              <div><span>失败任务</span><b>{{ formatNumber(stats?.failedTasks) }}</b><small>可在任务中心重试</small></div>
            </div>
          </div>
          <div class="page-toolbar" style="padding: 0 16px 14px">
            <button type="button" class="btn btn--sm" @click="go('/support/tickets')">处理工单</button>
            <button type="button" class="btn btn--sm" @click="go('/drugs/review')">药事审核</button>
            <button type="button" class="btn btn--sm" @click="ui.tasksOpen = true">任务中心</button>
          </div>
        </article>
      </section>
      <p class="faint" style="margin-top: 8px">原型分析仅使用当前内存种子数据，统计口径不构成正式经营结论。</p>
    </template>
  </section>
</template>

<style scoped>
.analytics-page { min-width: 0; }
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(8,181,120,.25); border-top-color: var(--c-brand); border-radius: 50%; animation: spin .8s linear infinite; }
</style>
